/**
 * Host-side wallet relay. Browser-wallet extensions (Phantom, Solflare,
 * Backpack) inject their providers only into top-level pages, never into
 * cross-origin iframes. The invest dialog therefore proxies wallet
 * detect/connect/sign requests to the SDK, which runs on the partner page
 * where `window.phantom` and friends actually exist.
 *
 * Zero-dep on purpose: the provider surface is typed via a narrow local
 * interface and signatures are base58-encoded with a small inline encoder.
 */

/** The tiny slice of a Solana wallet provider the relay needs. */
interface SolanaProvider {
  connect(): Promise<unknown>;
  signMessage(message: Uint8Array, encoding?: string): Promise<unknown>;
  publicKey?: { toString(): string } | null;
}

interface WalletDescriptor {
  id: string;
  name: string;
  /** Probes the page for the provider; undefined when not installed. */
  getProvider: () => SolanaProvider | undefined;
}

// biome-ignore lint/suspicious/noExplicitAny: wallet extensions patch window ad hoc.
const win = (): any => window as any;

const WALLETS: readonly WalletDescriptor[] = [
  { id: 'phantom', name: 'Phantom', getProvider: () => win().phantom?.solana },
  { id: 'solflare', name: 'Solflare', getProvider: () => win().solflare },
  {
    id: 'backpack',
    name: 'Backpack',
    getProvider: () => win().backpack?.solana,
  },
];

/** The supported wallet id/name pairs, without the provider probes.
 *
 * ⚠️ Mirrored by `SUPPORTED_WALLETS` in the app's `lib/invest-embed/
 * local-wallet-relay.ts`: the framed dialog relays through THIS list while a
 * top-level surface uses that one, so a wallet present in only one appears or
 * disappears depending on how the partner opened the flow. Exported so a
 * contract test can lock it on each side — see `wallet-relay.test.ts`. */
export const SUPPORTED_WALLETS: readonly { id: string; name: string }[] =
  WALLETS.map(({ id, name }) => ({ id, name }));

export type WalletErrorCode =
  | 'wallet_unavailable'
  | 'connect_failed'
  | 'sign_failed'
  | 'user_rejected';

/** Relay failure with a protocol error code attached. */
export class WalletRelayError extends Error {
  readonly code: WalletErrorCode;

  constructor(code: WalletErrorCode, message: string) {
    super(message);
    this.name = 'WalletRelayError';
    this.code = code;
  }
}

/** Wallet extensions present on the partner page right now. */
export function detectWallets(): { id: string; name: string }[] {
  return WALLETS.filter((wallet) => Boolean(wallet.getProvider())).map(
    ({ id, name }) => ({ id, name }),
  );
}

function requireProvider(walletId: string): SolanaProvider {
  const provider = WALLETS.find((w) => w.id === walletId)?.getProvider();
  if (!provider) {
    throw new WalletRelayError(
      'wallet_unavailable',
      `Wallet '${walletId}' is not available on this page`,
    );
  }
  return provider;
}

/** Connects the wallet and resolves the base58 address. */
export async function connectWallet(walletId: string): Promise<string> {
  const provider = requireProvider(walletId);
  const resp = (await provider.connect()) as
    | { publicKey?: { toString(): string } | null }
    | null
    | undefined;
  const publicKey = resp?.publicKey ?? provider.publicKey;
  if (!publicKey) {
    throw new WalletRelayError(
      'connect_failed',
      'Wallet did not return an address',
    );
  }
  return publicKey.toString();
}

/** Signs a UTF-8 message and resolves the base58-encoded signature. */
export async function signWalletMessage(
  walletId: string,
  message: string,
): Promise<string> {
  const provider = requireProvider(walletId);
  const result = (await provider.signMessage(
    new TextEncoder().encode(message),
    'utf8',
  )) as { signature?: Uint8Array } | Uint8Array | null | undefined;
  // Most wallets resolve { signature: Uint8Array }; some return the bytes
  // directly.
  const signature =
    result instanceof Uint8Array ? result : (result?.signature ?? null);
  if (!(signature instanceof Uint8Array)) {
    throw new WalletRelayError(
      'sign_failed',
      'Wallet did not return a signature',
    );
  }
  return encodeBase58(signature);
}

// ── Transaction signing (Wallet Standard) ────────────────────────────────
// The legacy injected providers (`window.phantom.solana` etc.) only sign
// web3.js Transaction OBJECTS, which a zero-dep loader can't construct. The
// Wallet Standard's `solana:signTransaction` feature signs raw serialized
// bytes, so the dialog sends base64 bytes and gets base64 bytes back.

/** Minimal Wallet Standard surface the relay needs. */
interface StandardWalletAccount {
  address: string;
  chains?: readonly string[];
}

interface StandardWallet {
  name: string;
  accounts: readonly StandardWalletAccount[];
  features: Record<string, unknown>;
}

/** Relay wallet id → the name the extension registers under in the Wallet
 * Standard. DERIVED from `WALLETS` rather than a third hand-kept copy of the
 * same mapping — the display name and the Wallet Standard name are the same
 * string for every wallet the relay supports, and the two lists drifting would
 * silently break transaction signing for whichever wallet was missed. */
const STANDARD_WALLET_NAMES: Record<string, string> = Object.fromEntries(
  WALLETS.map(({ id, name }) => [id, name]),
);

/**
 * Collects Wallet Standard wallets synchronously: wallets already injected
 * answer the `app-ready` bootstrap event by calling `register` before the
 * dispatch returns. No listener is left behind.
 */
function getStandardWallet(walletId: string): StandardWallet | undefined {
  const name = STANDARD_WALLET_NAMES[walletId];
  if (!name) return undefined;
  const found: StandardWallet[] = [];
  window.dispatchEvent(
    new CustomEvent('wallet-standard:app-ready', {
      detail: {
        register: (...wallets: StandardWallet[]) => {
          found.push(...wallets);
          return () => {};
        },
      },
    }),
  );
  return found.find((wallet) => wallet.name === name);
}

interface SignTransactionFeature {
  signTransaction(input: {
    transaction: Uint8Array;
    account: StandardWalletAccount;
    chain?: string;
  }): Promise<readonly { signedTransaction: Uint8Array }[]>;
}

/**
 * Signs a base64-serialized transaction with the Wallet Standard account at
 * `address` (falling back to the wallet's active account) and resolves the
 * SIGNED transaction re-serialized to base64.
 */
export async function signWalletTransaction(
  walletId: string,
  transactionBase64: string,
  address?: string,
): Promise<string> {
  const wallet = getStandardWallet(walletId);
  const feature = wallet?.features['solana:signTransaction'] as
    | SignTransactionFeature
    | undefined;
  if (!wallet || typeof feature?.signTransaction !== 'function') {
    throw new WalletRelayError(
      'sign_failed',
      `Wallet '${walletId}' cannot sign transactions on this page`,
    );
  }

  let account =
    (address && wallet.accounts.find((a) => a.address === address)) ||
    wallet.accounts[0];
  if (!account) {
    // Standard-side state can lag a legacy-provider connect; one explicit
    // standard connect resolves the account list.
    const connectFeature = wallet.features['standard:connect'] as
      | {
          connect(): Promise<{ accounts: readonly StandardWalletAccount[] }>;
        }
      | undefined;
    if (typeof connectFeature?.connect === 'function') {
      const result = await connectFeature.connect();
      account =
        (address && result.accounts.find((a) => a.address === address)) ||
        result.accounts[0];
    }
  }
  if (!account) {
    throw new WalletRelayError(
      'connect_failed',
      'Wallet has no connected account to sign with',
    );
  }

  const results = await feature.signTransaction({
    transaction: decodeBase64(transactionBase64),
    account,
    chain:
      account.chains?.find((chain) => chain.startsWith('solana:')) ??
      'solana:mainnet',
  });
  const signed = results[0]?.signedTransaction;
  if (!(signed instanceof Uint8Array)) {
    throw new WalletRelayError(
      'sign_failed',
      'Wallet did not return a signed transaction',
    );
  }
  return encodeBase64(signed);
}

/**
 * Classifies a relay failure into a protocol error code plus a short
 * message. Rejection-looking errors (message mentioning "reject", or the
 * EIP-1193-style code 4001 that Solana wallets reuse) map to
 * `user_rejected`; anything else keeps the caller's fallback code.
 */
export function mapWalletError(
  error: unknown,
  fallback: WalletErrorCode,
): { code: WalletErrorCode; message: string } {
  if (error instanceof WalletRelayError) {
    return { code: error.code, message: error.message };
  }
  const message =
    error instanceof Error ? error.message : String(error ?? 'Unknown error');
  const numericCode =
    typeof error === 'object' && error !== null
      ? (error as { code?: unknown }).code
      : undefined;
  if (/reject/i.test(message) || numericCode === 4001) {
    return { code: 'user_rejected', message };
  }
  return { code: fallback, message };
}

/** Base64 for transaction bytes over postMessage (strings only). */
function encodeBase64(bytes: Uint8Array): string {
  let binary = '';
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary);
}

function decodeBase64(value: string): Uint8Array {
  const binary = atob(value);
  const out = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) out[i] = binary.charCodeAt(i);
  return out;
}

const BASE58_ALPHABET =
  '123456789ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz';

/** Base58 (Bitcoin alphabet) encoder; leading zero bytes become '1's. */
export function encodeBase58(bytes: Uint8Array): string {
  let zeros = 0;
  while (zeros < bytes.length && bytes[zeros] === 0) zeros++;

  // Base-256 to base-58 long division, little-endian digit accumulator.
  const digits: number[] = [];
  for (let i = zeros; i < bytes.length; i++) {
    let carry = bytes[i] as number;
    for (let j = 0; j < digits.length; j++) {
      carry += (digits[j] as number) * 256;
      digits[j] = carry % 58;
      carry = Math.floor(carry / 58);
    }
    while (carry > 0) {
      digits.push(carry % 58);
      carry = Math.floor(carry / 58);
    }
  }

  let out = '1'.repeat(zeros);
  for (let i = digits.length - 1; i >= 0; i--) {
    out += BASE58_ALPHABET[digits[i] as number];
  }
  return out;
}
